/**
 * Debt Projection Engine
 * 
 * Models enhanced debt accounts with proper repayment schedules, interest calculations,
 * and multiple debt strategy support (avalanche, snowball, custom priority).
 */

import { EnhancedDebtConfiguration, DebtAccount } from '@/types/enhancedDebt';
import { logger } from '@/lib/utils/logger';

export interface DebtRepaymentResult {
  principal: number;
  interest: number;
  remaining: number;
  byAccount: Record<string, {
    balance: number;
    interestPaid: number;
    principalPaid: number;
    status: 'active' | 'paid-off';
  }>;
}

export class DebtProjectionEngine {
  /**
   * Calculate debt repayments for a given year
   * Handles multiple debt accounts with proper interest and principal allocation
   */
  static calculateDebtRepayments(
    enhancedDebt: EnhancedDebtConfiguration,
    year: number,
    currentBalances?: Record<string, number>
  ): DebtRepaymentResult {
    if (!enhancedDebt.enabled || !enhancedDebt.accounts || enhancedDebt.accounts.length === 0) {
      return {
        principal: 0,
        interest: 0,
        remaining: 0,
        byAccount: {}
      };
    }

    // Initialize balances if not provided
    const balances = currentBalances || this.initializeBalances(enhancedDebt.accounts);
    
    let totalPrincipal = 0;
    let totalInterest = 0;
    let totalRemaining = 0;
    const byAccount: DebtRepaymentResult['byAccount'] = {};

    // Process each active account
    for (const account of enhancedDebt.accounts) {
      if (!account.enabled) continue;

      const currentBalance = balances[account.id] || account.balance;
      
      // Skip if already paid off
      if (currentBalance <= 0) {
        byAccount[account.id] = {
          balance: 0,
          interestPaid: 0,
          principalPaid: 0,
          status: 'paid-off'
        };
        continue;
      }

      // Calculate annual interest
      const annualInterest = currentBalance * (account.interestRate / 100);
      
      // Calculate total annual repayment (12 months)
      const annualRepayment = account.monthlyRepayment * 12;
      
      // Handle different repayment types
      let principalPaid = 0;
      let interestPaid = 0;
      let newBalance = currentBalance;

      switch (account.repaymentType) {
        case 'interest-only':
          // Interest-only: pay only interest, principal unchanged
          interestPaid = Math.min(annualInterest, annualRepayment);
          principalPaid = Math.max(0, annualRepayment - interestPaid);
          newBalance = Math.max(0, currentBalance - principalPaid);
          break;

        case 'minimum-payment':
          // Minimum payment (credit cards): interest first, then principal
          interestPaid = Math.min(annualInterest, annualRepayment);
          principalPaid = Math.max(0, annualRepayment - interestPaid);
          newBalance = Math.max(0, currentBalance - principalPaid);
          break;

        case 'principal-interest':
        default:
          // Standard P&I: calculate using amortization formula
          const result = this.calculateAmortization(
            currentBalance,
            account.interestRate / 100,
            annualRepayment
          );
          interestPaid = result.interest;
          principalPaid = result.principal;
          newBalance = result.newBalance;
          break;
      }

      // Apply offset account if present (reduces interest)
      if (account.offsetBalance && account.offsetBalance > 0) {
        const effectiveBalance = Math.max(0, currentBalance - account.offsetBalance);
        const interestSaving = (currentBalance - effectiveBalance) * (account.interestRate / 100);
        interestPaid = Math.max(0, interestPaid - interestSaving);
      }

      // Update totals
      totalPrincipal += principalPaid;
      totalInterest += interestPaid;
      totalRemaining += newBalance;

      byAccount[account.id] = {
        balance: newBalance,
        interestPaid,
        principalPaid,
        status: newBalance <= 0 ? 'paid-off' : 'active'
      };

      // Update balance for next iteration
      balances[account.id] = newBalance;
    }

    // Apply extra repayments based on strategy
    if (enhancedDebt.repaymentStrategy && enhancedDebt.repaymentStrategy.extraRepayment > 0) {
      const extraResult = this.applyExtraRepayments(
        enhancedDebt,
        balances,
        byAccount
      );
      totalPrincipal += extraResult.extraPrincipal;
      totalRemaining = extraResult.newTotalRemaining;
    }

    logger.info(`💰 Year ${year} debt repayments:`, {
      totalPrincipal: totalPrincipal.toFixed(2),
      totalInterest: totalInterest.toFixed(2),
      totalRemaining: totalRemaining.toFixed(2),
      activeAccounts: Object.values(byAccount).filter(a => a.status === 'active').length
    });

    return {
      principal: totalPrincipal,
      interest: totalInterest,
      remaining: totalRemaining,
      byAccount
    };
  }

  /**
   * Initialize balances from accounts
   */
  private static initializeBalances(accounts: DebtAccount[]): Record<string, number> {
    const balances: Record<string, number> = {};
    for (const account of accounts) {
      if (account.enabled) {
        balances[account.id] = account.balance;
      }
    }
    return balances;
  }

  /**
   * Calculate amortization for principal & interest loans
   */
  private static calculateAmortization(
    balance: number,
    annualRate: number,
    annualPayment: number
  ): { principal: number; interest: number; newBalance: number } {
    // Calculate annual interest
    const interest = balance * annualRate;
    
    // Principal is payment minus interest
    const principal = Math.min(annualPayment - interest, balance);
    
    // New balance
    const newBalance = Math.max(0, balance - principal);
    
    return {
      principal: Math.max(0, principal),
      interest: Math.max(0, interest),
      newBalance
    };
  }

  /**
   * Apply extra repayments according to strategy
   */
  private static applyExtraRepayments(
    enhancedDebt: EnhancedDebtConfiguration,
    balances: Record<string, number>,
    byAccount: DebtRepaymentResult['byAccount']
  ): { extraPrincipal: number; newTotalRemaining: number } {
    const strategy = enhancedDebt.repaymentStrategy;
    const extraAnnual = strategy.extraRepayment * 12;
    let remainingExtra = extraAnnual;
    let extraPrincipal = 0;

    // Determine priority order
    let priorityAccounts: DebtAccount[] = [];
    
    switch (strategy.type) {
      case 'avalanche':
        // Highest interest rate first
        priorityAccounts = [...enhancedDebt.accounts].sort(
          (a, b) => b.interestRate - a.interestRate
        );
        break;

      case 'snowball':
        // Lowest balance first
        priorityAccounts = [...enhancedDebt.accounts].sort(
          (a, b) => (balances[a.id] || 0) - (balances[b.id] || 0)
        );
        break;

      case 'custom':
        // Use priority order from strategy
        priorityAccounts = strategy.priorityOrder
          .map(id => enhancedDebt.accounts.find(a => a.id === id))
          .filter(a => a !== undefined) as DebtAccount[];
        break;
    }

    // Apply extra payments to priority accounts
    for (const account of priorityAccounts) {
      if (!account.enabled || remainingExtra <= 0) continue;
      
      const currentBalance = balances[account.id] || 0;
      if (currentBalance <= 0) continue;

      const extraForThisAccount = Math.min(remainingExtra, currentBalance);
      balances[account.id] = currentBalance - extraForThisAccount;
      byAccount[account.id].balance = balances[account.id];
      byAccount[account.id].principalPaid += extraForThisAccount;
      
      if (balances[account.id] <= 0) {
        byAccount[account.id].status = 'paid-off';
      }

      extraPrincipal += extraForThisAccount;
      remainingExtra -= extraForThisAccount;
    }

    const newTotalRemaining = Object.values(balances).reduce((sum, bal) => sum + bal, 0);

    return {
      extraPrincipal,
      newTotalRemaining
    };
  }

  /**
   * Project full debt schedule over multiple years
   */
  static projectDebtSchedule(
    enhancedDebt: EnhancedDebtConfiguration,
    years: number
  ): DebtRepaymentResult[] {
    const schedule: DebtRepaymentResult[] = [];
    const balances = this.initializeBalances(enhancedDebt.accounts);

    for (let year = 0; year < years; year++) {
      const result = this.calculateDebtRepayments(enhancedDebt, year, balances);
      schedule.push(result);

      // Update balances for next year
      for (const accountId in result.byAccount) {
        balances[accountId] = result.byAccount[accountId].balance;
      }

      // Stop if all debts paid off
      if (result.remaining <= 0) {
        logger.info(`✅ All debts paid off after ${year + 1} years`);
        break;
      }
    }

    return schedule;
  }
}
